/**
 * SharedRegisterPage — unified registration for both Lite (plan 1) and Pro (plan 2).
 * After a successful registration the JWT token is decoded to read the `plan` field:
 *   plan === 2  →  redirect to /brand  (Pro CMS)
 *   plan === 1  →  redirect to /lite   (Lite CMS)  [default if field missing]
 *
 * The original per-mode pages are preserved as *.bak.js backups.
 */

import React, { useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { Form, Input, Button, message } from "antd";
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { motion, AnimatePresence } from "framer-motion";
import ReCAPTCHA from "react-google-recaptcha";
import {
  requestLiteRegister,
  loadUserData,
  requestServerPostSocialLogin,
  selectPage,
} from "../actions";
import { signInWithGoogle } from "./lite/googleAuth";
import {
  MonitorPlay,
  CalendarClock,
  BarChart2,
  Megaphone,
  Layers,
} from "lucide-react";
import "./login_lite.css";
import Constants from "../data/constants";
import StringHelper from "../helpers/StringHelper";

// ── Step config ──────────────────────────────────────────────────
const STEPS = [
  { label: "Account", key: "account" },
  { label: "Profile", key: "profile" },
  { label: "Done", key: "done" },
];

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
  center: { x: 0, opacity: 1, transition: { duration: 0.38, ease: "easeOut" } },
  exit: (dir) => ({
    x: dir > 0 ? -60 : 60,
    opacity: 0,
    transition: { duration: 0.25 },
  }),
};

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" },
  }),
};

const FEATURES = [
  {
    icon: MonitorPlay,
    title: "Unlimited Screens",
    desc: "Manage any number of screens across all your locations.",
  },
  {
    icon: Layers,
    title: "Multi-Zone Layouts",
    desc: "Design complex multi-region layouts with full precision.",
  },
  {
    icon: CalendarClock,
    title: "Advanced Scheduling",
    desc: "Day-parting, event triggers, and custom content rules.",
  },
  {
    icon: BarChart2,
    title: "Analytics & Reports",
    desc: "Monitor uptime, content performance, and audit logs.",
  },
  {
    icon: Megaphone,
    title: "Campaign Management",
    desc: "Run time-targeted campaigns across all locations.",
  },
];

// ── Password strength ────────────────────────────────────────────
const getPwChecks = (val = "") => ({
  length: val.length >= 8,
  upper: /[A-Z]/.test(val),
  lower: /[a-z]/.test(val),
  digit: /[0-9]/.test(val),
  special: /[^A-Za-z0-9]/.test(val),
});

const calcStrength = (val) => {
  if (!val) return 0;
  const c = getPwChecks(val);
  return [c.length, c.upper || c.lower, c.digit, c.special].filter(Boolean)
    .length;
};

const STRENGTH_META = [
  null,
  { label: "Weak", color: "#ef4444" },
  { label: "Fair", color: "#f97316" },
  { label: "Good", color: "#eab308" },
  { label: "Strong", color: "#22c55e" },
];

const PW_REQUIREMENTS = [
  { key: "length", label: "At least 8 characters" },
  { key: "upper", label: "Uppercase letter (A–Z)" },
  { key: "lower", label: "Lowercase letter (a–z)" },
  { key: "digit", label: "Number (0–9)" },
  { key: "special", label: "Special character (!@#…)" },
];

// ── Redirect helper ───────────────────────────────────────────────
function redirectByPlan(token, history, selectPage) {
  const plan = StringHelper.getJWTPlan(token);
  if (plan === 2) {
    selectPage(Constants.BRAND_PAGE);
    history.push(`/${Constants.BRAND_PAGE}`);
  } else {
    history.push("/lite");
  }
}

// ── Component ────────────────────────────────────────────────────
function SharedRegisterPage({
  requestLiteRegister,
  loadUserData,
  requestServerPostSocialLogin,
  selectPage,
}) {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [formData, setFormData] = useState({});
  const [captchaDone, setCaptchaDone] = useState(false);
  const [pwStrength, setPwStrength] = useState(0);
  const [pwValue, setPwValue] = useState("");
  const [registeredToken, setRegisteredToken] = useState(null);
  const captchaRef = useRef(null);
  const history = useHistory();
  const [form] = Form.useForm();

  const goNext = async () => {
    try {
      const vals = await form.validateFields();
      setFormData((prev) => ({ ...prev, ...vals }));
      setDir(1);
      setStep((s) => s + 1);
    } catch (_) {}
  };

  const goBack = () => {
    setDir(-1);
    setStep((s) => s - 1);
  };

  const onFinish = async () => {
    let vals;
    try {
      vals = await form.validateFields();
      setFormData((prev) => ({ ...prev, ...vals }));
    } catch (_) {
      return;
    }
    if (!captchaDone) {
      message.warning("Please complete the CAPTCHA verification.");
      return;
    }
    const captchaToken = captchaRef.current?.getValue();
    if (!captchaToken) {
      message.warning("Please complete the CAPTCHA verification.");
      return;
    }
    const payload = { ...formData, ...vals };
    setLoading(true);
    requestLiteRegister(
      "register",
      payload,
      (response) => {
        setLoading(false);
        if (response?.data?.success || response?.data?.token) {
          if (response?.data?.token) {
            loadUserData(response.data);
            setRegisteredToken(response.data.token);
          }
          setDir(1);
          setStep(2);
        } else {
          message.error(
            response?.data?.message || "Registration failed. Please try again.",
          );
        }
      },
      "",
      (err) => {
        setLoading(false);
        message.error(err?.message || "Registration failed. Please try again.");
      },
    );
  };

  const onGoogleSignUp = async () => {
    setLoading(true);
    try {
      const { idToken, email, displayName, photoURL, social_channel, uid } =
        await signInWithGoogle();
      requestServerPostSocialLogin(
        "social_auth",
        { idToken, email, displayName, photoURL, social_channel, uid },
        "google",
        (response) => {
          setLoading(false);
          if (response?.data?.token) {
            loadUserData(response.data);
            redirectByPlan(response.data.token, history, selectPage);
          } else {
            message.error("Google sign-up failed. Please try again.");
          }
        },
        "",
        (err) => {
          setLoading(false);
          message.error("Google sign-up failed. Please try again.");
        },
      );
    } catch (err) {
      setLoading(false);
      if (err.code === "auth/account-exists-with-different-credential") {
        message.warning(
          err.message ||
            "This email is already registered with another provider.",
        );
      } else if (err.code !== "auth/popup-closed-by-user") {
        message.error("Google sign-up failed. Please try again.");
      }
    }
  };

  // ── Inline styles ─────────────────────────────────────────────
  const inputStyle = {
    borderRadius: "10px",
    fontSize: "15px",
    border: "1px solid #e2e8f0",
  };

  const buttonStyle = {
    borderRadius: "10px",
    height: "48px",
    fontWeight: 600,
    fontSize: "16px",
    background: "linear-gradient(90deg, #4f46e5, #6366f1, #7c3aed)",
    border: "none",
    boxShadow: "0 4px 16px rgba(79,70,229,0.4)",
  };

  // ── Step 0: Account ─────────────────────────────────────────────
  const renderStep0 = () => (
    <motion.div
      key="step0"
      custom={dir}
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      style={{ width: "100%" }}
    >
      <Form.Item
        name="email"
        rules={[
          { required: true, message: "Please enter your email" },
          { type: "email", message: "Enter a valid email" },
        ]}
        style={{ marginBottom: 8 }}
      >
        <Input
          prefix={<MailOutlined style={{ color: "#cbd5e1" }} />}
          placeholder="Email Address"
          size="large"
          style={inputStyle}
        />
      </Form.Item>

      <Form.Item
        name="username"
        rules={[
          { required: true, message: "Please enter a username" },
          { whitespace: true, message: "Username cannot be blank" },
          {
            pattern: /^[a-zA-Z0-9_]+$/,
            message: "Letters, numbers, and underscores only",
          },
        ]}
        style={{ marginBottom: 8 }}
      >
        <Input
          prefix={<UserOutlined style={{ color: "#cbd5e1" }} />}
          placeholder="Username"
          size="large"
          style={inputStyle}
        />
      </Form.Item>

      <Form.Item
        name="password"
        rules={[
          { required: true, message: "Please enter a password" },
          {
            validator(_, value) {
              if (!value) return Promise.resolve();
              const c = getPwChecks(value);
              const missing = [];
              if (!c.length) missing.push("8+ characters");
              if (!c.upper) missing.push("uppercase letter");
              if (!c.lower) missing.push("lowercase letter");
              if (!c.digit) missing.push("number");
              if (!c.special) missing.push("special character");
              if (missing.length === 0) return Promise.resolve();
              return Promise.reject(
                new Error(`Missing: ${missing.join(", ")}`),
              );
            },
          },
        ]}
        style={{ marginBottom: 6 }}
      >
        <Input.Password
          prefix={<LockOutlined style={{ color: "#cbd5e1" }} />}
          placeholder="Password"
          size="large"
          style={inputStyle}
          onChange={(e) => {
            setPwStrength(calcStrength(e.target.value));
            setPwValue(e.target.value);
          }}
        />
      </Form.Item>

      {/* Password strength bar + requirement checklist */}
      <div
        style={{
          overflow: "hidden",
          maxHeight: pwValue.length > 0 ? 160 : 0,
          opacity: pwValue.length > 0 ? 1 : 0,
          marginBottom: pwValue.length > 0 ? 10 : 0,
          transition:
            "max-height 0.3s ease, opacity 0.3s ease, margin-bottom 0.3s ease",
        }}
      >
        {/* Requirement checklist */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "4px 12px",
            marginTop: 8,
          }}
        >
          {PW_REQUIREMENTS.map((req) => {
            const checks = getPwChecks(pwValue);
            const met = checks[req.key];
            return (
              <span
                key={req.key}
                style={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: met ? "#22c55e" : "#94a3b8",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  transition: "color 0.2s",
                }}
              >
                <span style={{ fontSize: 13, lineHeight: 1 }}>
                  {met ? "✓" : "○"}
                </span>
                {req.label}
              </span>
            );
          })}
        </div>
      </div>

      <Form.Item
        name="confirmPassword"
        dependencies={["password"]}
        rules={[
          { required: true, message: "Please confirm your password" },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue("password") === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error("Passwords do not match"));
            },
          }),
        ]}
        style={{ marginBottom: 14 }}
      >
        <Input.Password
          prefix={<LockOutlined style={{ color: "#cbd5e1" }} />}
          placeholder="Confirm Password"
          size="large"
          style={inputStyle}
        />
      </Form.Item>

      <Button
        type="primary"
        size="large"
        block
        onClick={goNext}
        className="lite-primary-btn"
        style={buttonStyle}
      >
        Continue <ArrowRightOutlined />
      </Button>
    </motion.div>
  );

  // ── Step 1: Profile ─────────────────────────────────────────────
  const renderStep1 = () => (
    <motion.div
      key="step1"
      custom={dir}
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      style={{ width: "100%" }}
    >
      <div style={{ display: "flex", gap: 10 }}>
        <Form.Item
          name="firstName"
          rules={[
            { required: true, message: "Please enter your first name" },
            { whitespace: true, message: "First name cannot be blank" },
          ]}
          style={{ marginBottom: 14, flex: 1 }}
        >
          <Input
            prefix={<UserOutlined style={{ color: "#cbd5e1" }} />}
            placeholder="First Name"
            size="large"
            style={inputStyle}
          />
        </Form.Item>
        <Form.Item
          name="lastName"
          rules={[
            { required: true, message: "Please enter your last name" },
            { whitespace: true, message: "Last name cannot be blank" },
          ]}
          style={{ marginBottom: 14, flex: 1 }}
        >
          <Input placeholder="Last Name" size="large" style={inputStyle} />
        </Form.Item>
      </div>

      <Form.Item name="company" style={{ marginBottom: 20 }}>
        <Input
          prefix={<UserOutlined style={{ color: "#cbd5e1" }} />}
          placeholder="Company / Organization (optional)"
          size="large"
          style={inputStyle}
        />
      </Form.Item>

      {/* reCAPTCHA */}
      <div
        style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}
      >
        <ReCAPTCHA
          ref={captchaRef}
          sitekey={
            process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
            "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
          }
          onChange={(token) => setCaptchaDone(!!token)}
          onExpired={() => setCaptchaDone(false)}
          onError={() => setCaptchaDone(false)}
        />
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <Button
          size="large"
          onClick={goBack}
          icon={<ArrowLeftOutlined />}
          style={{
            borderRadius: 10,
            height: 48,
            border: "1px solid #e2e8f0",
            color: "#64748b",
            background: "#fff",
          }}
        >
          Back
        </Button>
        <Button
          type="primary"
          size="large"
          onClick={onFinish}
          loading={loading}
          disabled={!captchaDone}
          className="lite-primary-btn"
          style={{ ...buttonStyle, flex: 1 }}
        >
          Create Account
        </Button>
      </div>
    </motion.div>
  );

  // ── Step 2: Done ────────────────────────────────────────────────
  const renderStep2 = () => (
    <motion.div
      key="step2"
      custom={dir}
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      style={{ width: "100%", textAlign: "center" }}
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 14 }}
        style={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          background: "rgba(99,102,241,0.12)",
          border: "2px solid rgba(99,102,241,0.3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 20px",
        }}
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{
            delay: 0.2,
            type: "spring",
            stiffness: 260,
            damping: 18,
          }}
          style={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            background: "linear-gradient(135deg,#6366f1,#7c3aed)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 22,
            color: "#fff",
            fontWeight: 700,
          }}
        >
          ✓
        </motion.div>
      </motion.div>

      <motion.h3
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: "#0f172a",
          margin: "0 0 10px",
        }}
      >
        You're all set!
      </motion.h3>

      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
        style={{
          fontSize: 14,
          color: "#64748b",
          margin: "0 0 28px",
          lineHeight: 1.6,
        }}
      >
        Your account has been created. Continue to start managing your screens.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55 }}
      >
        {registeredToken ? (
          <Button
            type="primary"
            size="large"
            block
            className="lite-primary-btn"
            style={{ ...buttonStyle }}
            onClick={() => redirectByPlan(registeredToken, history, selectPage)}
          >
            Get Started <ArrowRightOutlined />
          </Button>
        ) : (
          <Button
            type="primary"
            size="large"
            block
            className="lite-primary-btn"
            style={{ ...buttonStyle }}
            onClick={() => history.push("/login")}
          >
            Go to Sign In <ArrowRightOutlined />
          </Button>
        )}
      </motion.div>
    </motion.div>
  );

  return (
    <div
      className="lite-page"
      style={{ fontFamily: "'Kanit', 'Inter', -apple-system, sans-serif" }}
    >
      {/* ── Left panel ── */}
      <div
        className="lite-left"
        style={{
          position: "relative",
          background:
            "linear-gradient(160deg, #06061a 0%, #0e0e2e 40%, #0a0a20 70%, #06061a 100%)",
          overflow: "hidden",
        }}
      >
        {/* Glows */}
        <div
          style={{
            position: "absolute",
            width: "560px",
            height: "560px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 65%)",
            top: "-140px",
            left: "-180px",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: "400px",
            height: "400px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 65%)",
            bottom: "-100px",
            right: "-100px",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: "200px",
            height: "200px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(139,92,246,0.2) 0%, transparent 70%)",
            top: "45%",
            left: "55%",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
            pointerEvents: "none",
          }}
        />

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          style={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            maxWidth: "500px",
            padding: "40px 36px",
          }}
        >
          <motion.div
            custom={0}
            variants={fadeUp}
            initial="initial"
            animate="animate"
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "5px 14px",
                borderRadius: "20px",
                background: "rgba(99,102,241,0.12)",
                border: "1px solid rgba(99,102,241,0.28)",
                marginBottom: "28px",
              }}
            >
              <span
                style={{
                  color: "#a5b4fc",
                  fontSize: "13px",
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                }}
              >
                ✦ CodexSignage Platform
              </span>
            </div>
          </motion.div>

          <motion.h1
            custom={1}
            variants={fadeUp}
            initial="initial"
            animate="animate"
            style={{
              fontSize: "clamp(38px, 4.5vw, 60px)",
              fontWeight: 800,
              color: "#ffffff",
              margin: "0 0 16px",
              lineHeight: 1.05,
              letterSpacing: "-0.03em",
            }}
          >
            CodexSignage
            <br />
            <span
              style={{
                background:
                  "linear-gradient(135deg, #818cf8 0%, #6366f1 40%, #4f46e5 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              CMS.
            </span>
          </motion.h1>

          <motion.p
            custom={2}
            variants={fadeUp}
            initial="initial"
            animate="animate"
            style={{
              fontSize: "clamp(14px, 1.8vw, 16px)",
              color: "rgba(255,255,255,0.5)",
              margin: "0 0 32px",
              lineHeight: 1.7,
              maxWidth: "400px",
            }}
          >
            The complete digital signage CMS — manage screens, campaigns,
            schedules, and analytics across all your locations.
          </motion.p>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  custom={i + 3}
                  variants={fadeUp}
                  initial="initial"
                  animate="animate"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "14px",
                    padding: "10px 14px",
                    borderRadius: "10px",
                    background: "rgba(99,102,241,0.07)",
                    border: "1px solid rgba(99,102,241,0.15)",
                  }}
                >
                  <div
                    style={{
                      width: "34px",
                      height: "34px",
                      borderRadius: "8px",
                      background: "rgba(99,102,241,0.2)",
                      border: "1px solid rgba(99,102,241,0.3)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={16} color="#a5b4fc" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#f1f5f9",
                        marginBottom: "1px",
                      }}
                    >
                      {f.title}
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "rgba(255,255,255,0.38)",
                        lineHeight: 1.4,
                      }}
                    >
                      {f.desc}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            custom={9}
            variants={fadeUp}
            initial="initial"
            animate="animate"
            style={{
              marginTop: "24px",
              fontSize: "11px",
              color: "rgba(255,255,255,0.2)",
              letterSpacing: "0.06em",
              textAlign: "center",
            }}
          >
            TRUSTED BY SIGNAGE PROFESSIONALS WORLDWIDE
          </motion.div>
        </motion.div>
      </div>

      {/* ── Right panel: register form ── */}
      <div className="lite-right">
        <motion.div
          style={{
            width: "100%",
            maxWidth: "440px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "48px 40px",
          }}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
        >
          <img
            src="/cms/assets/svgs/cdx_logo.svg"
            alt="CodexSignage"
            style={{
              height: "52px",
              objectFit: "contain",
              marginBottom: "20px",
            }}
          />

          <h2
            style={{
              fontSize: "28px",
              fontWeight: 700,
              color: "#0f172a",
              margin: "0 0 6px",
              letterSpacing: "-0.02em",
              width: "100%",
            }}
          >
            {step === 2
              ? "Welcome aboard 🎉"
              : step === 1
                ? "Tell us about you"
                : "Create your account"}
          </h2>
          <p
            style={{
              fontSize: "14px",
              color: "#94a3b8",
              margin: "0 0 24px",
              width: "100%",
            }}
          >
            {step === 2
              ? ""
              : step === 1
                ? "Almost there — just a few details"
                : "Get started with your account"}
          </p>

          {/* Step indicators */}
          {step < 2 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 24,
                width: "100%",
              }}
            >
              {STEPS.slice(0, 2).map((s, i) => (
                <React.Fragment key={s.key}>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <motion.div
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        background:
                          i <= step
                            ? "linear-gradient(135deg,#6366f1,#7c3aed)"
                            : "#e5e7eb",
                        color: i <= step ? "#fff" : "#9ca3af",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                      animate={{ scale: i === step ? [1, 1.15, 1] : 1 }}
                      transition={{
                        duration: 0.6,
                        repeat: i === step ? Infinity : 0,
                        repeatDelay: 1.4,
                      }}
                    >
                      {i < step ? "✓" : i + 1}
                    </motion.div>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: i <= step ? "#374151" : "#9ca3af",
                      }}
                    >
                      {s.label}
                    </span>
                  </div>
                  {i < 1 && (
                    <div
                      style={{
                        flex: 1,
                        height: 2,
                        borderRadius: 1,
                        background: i < step ? "#6366f1" : "#e5e7eb",
                        transition: "background 0.4s",
                      }}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Progress bar */}
          {step < 2 && (
            <div
              style={{
                width: "100%",
                height: 3,
                borderRadius: 2,
                background: "#f1f5f9",
                marginBottom: 24,
                overflow: "hidden",
              }}
            >
              <motion.div
                style={{
                  height: "100%",
                  borderRadius: 2,
                  background: "linear-gradient(90deg, #6366f1, #7c3aed)",
                }}
                animate={{ width: `${(step / 1) * 100}%` }}
                transition={{ duration: 0.4 }}
              />
            </div>
          )}

          <Form form={form} layout="vertical" style={{ width: "100%" }}>
            {/* Google sign-up — only on step 0 */}
            {step === 0 && (
              <>
                <button
                  type="button"
                  disabled={loading}
                  onClick={onGoogleSignUp}
                  className="lite-social-btn"
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                    padding: "10px 16px",
                    borderRadius: 10,
                    border: "1px solid #e2e8f0",
                    background: "#fff",
                    cursor: loading ? "not-allowed" : "pointer",
                    fontSize: 15,
                    fontWeight: 500,
                    color: "#0f172a",
                    marginBottom: 16,
                  }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 48 48"
                    style={{ flexShrink: 0 }}
                  >
                    <path
                      fill="#EA4335"
                      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.05 17.74 9.5 24 9.5z"
                    />
                    <path
                      fill="#4285F4"
                      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                    />
                    <path
                      fill="#34A853"
                      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-3.55-13.46-8.9l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                    />
                  </svg>
                  Continue with Google
                </button>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 16,
                  }}
                >
                  <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
                  <span
                    style={{
                      fontSize: 12,
                      color: "#94a3b8",
                      whiteSpace: "nowrap",
                    }}
                  >
                    or sign up with email
                  </span>
                  <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
                </div>
              </>
            )}
            <AnimatePresence mode="wait" custom={dir}>
              {step === 0 && renderStep0()}
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
            </AnimatePresence>
          </Form>

          {/* Footer */}
          {step < 2 && (
            <p
              style={{
                marginTop: 24,
                fontSize: 13,
                color: "#94a3b8",
                textAlign: "center",
              }}
            >
              Already have an account?{" "}
              <span
                style={{ color: "#6366f1", cursor: "pointer", fontWeight: 600 }}
                onClick={() => history.push("/login")}
              >
                Sign in
              </span>
            </p>
          )}
        </motion.div>
      </div>
    </div>
  );
}

const mapDispatchToProps = (dispatch) => ({
  requestLiteRegister: (...args) => dispatch(requestLiteRegister(...args)),
  requestServerPostSocialLogin: (...args) =>
    dispatch(requestServerPostSocialLogin(...args)),
  loadUserData: (data) => dispatch(loadUserData(data)),
  selectPage: (page) => dispatch(selectPage(page)),
});

export default connect(null, mapDispatchToProps)(SharedRegisterPage);
